import { Check, ChevronRight, Loader2, Wrench } from "lucide-react";
import { activitySummaryLabel } from "./activity-groups";
import { formatEventAuthor, formatEventTime } from "./event-utils";
import type { ActivitySummary } from "./message-utils";

type AgentActivitySummaryProps = {
  activity: ActivitySummary;
};

export function AgentActivitySummary({ activity }: AgentActivitySummaryProps) {
  const isRunning = activity.active.length > 0;

  return (
    <article
      aria-label={`${activity.phase.title} activity from ${formatEventAuthor(activity.author)}`}
      className="rounded-xl border border-slate-200 bg-white px-4 py-3"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
          {isRunning ? (
            <Loader2
              aria-hidden="true"
              className="size-4 animate-spin motion-reduce:animate-none"
            />
          ) : (
            <Wrench aria-hidden="true" className="size-4" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-900 text-sm">
            {activity.phase.title}
          </p>
          <p className="text-slate-500 text-xs">
            {activitySummaryLabel(activity)}
            {activity.createdAt
              ? ` · ${formatEventTime(activity.createdAt)}`
              : ""}
          </p>
        </div>
      </div>

      <details className="group mt-3">
        <summary className="flex cursor-pointer list-none items-center gap-1 text-slate-600 text-xs focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
          <ChevronRight
            aria-hidden="true"
            className="size-3 transition-transform group-open:rotate-90 motion-reduce:transition-none"
          />
          View details
        </summary>
        <ul className="mt-2 space-y-1.5 border-slate-100 border-t pt-2 text-slate-600 text-xs">
          {activity.completed.map((tool) => (
            <li className="flex items-center gap-2" key={tool.id}>
              <Check aria-hidden="true" className="size-3 text-emerald-600" />
              {tool.content.label}
            </li>
          ))}
          {activity.active.map((tool) => (
            <li className="flex items-center gap-2" key={tool.id}>
              <Loader2
                aria-hidden="true"
                className="size-3 animate-spin motion-reduce:animate-none"
              />
              {tool.content.label}
            </li>
          ))}
        </ul>
      </details>
    </article>
  );
}
